import * as React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Box,
  Stack,
  MenuItem,
  Select,
  FormControl,
  FormControlLabel,
  InputLabel,
  FormHelperText,
  Checkbox,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  DiagnosisInterventionsReadDTO,
  DiagnosisInterventionsCreateDTO,
  IDiagnosisInterventionsCreateDTO,
} from '../../../generated/api-client';
import { apiClient, fetchEnergyUsesByVector } from '../../../services/apiClient';
import { ActionButton } from '../../common';
import { EnergyUseDropdown, ALL_ENERGY_USE_VALUE } from '../../common/EnergyUseDropdown';
import { EnergyVectorDropdown } from '../../common/EnergyVectorDropdown';
import { getEnergyVectorOptionLabel } from '../../common/EnergyVectorDropdown';
import { useVectorTypeOptions } from '../../../hooks/useVectorTypeOptions';
import { useDiagnosisEnergyVectorsByDiagnosisId } from '../../../hooks';

interface EditInterventionDialogProps {
  open: boolean;
  onClose: () => void;
  intervention: DiagnosisInterventionsReadDTO | null;
  diagnosisId: string;
}

type InterventionFormValues = Omit<
  IDiagnosisInterventionsCreateDTO,
  'annual_energy_saving_by_vector'
> & {
  annual_energy_saving_by_vector?: { [key: string]: number | null };
};

function sanitizeAnnualSavingMap(map?: {
  [key: string]: number | null;
}): { [key: string]: number } | undefined {
  if (!map) return undefined;
  const result: { [key: string]: number } = {};
  for (const [key, value] of Object.entries(map)) {
    if (value != null) result[key] = value;
  }
  return Object.keys(result).length ? result : undefined;
}

const PRIORITY_OPTIONS = ['alta', 'media', 'bassa', 'Da valutare'];
const TYPE_OPTIONS = ['tecnologico', 'gestionale', 'procedure'];

export const EditInterventionDialog: React.FC<EditInterventionDialogProps> = ({
  open,
  onClose,
  intervention,
  diagnosisId,
}) => {
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    getValues,
    formState: { errors, isDirty },
  } = useForm<InterventionFormValues>({
    defaultValues: intervention
      ? ({
          diagnosis_id: diagnosisId,
          code: (intervention as any).code ?? undefined,
          energy_vectors: intervention.energy_vectors ?? [],
          title: intervention.title ?? undefined,
          cost_before_ea: intervention.cost_before_ea ?? undefined,
          energy_uses: intervention.energy_uses ?? [],
          all_energy_use: intervention.all_energy_use ?? false,
          description: intervention.description ?? undefined,
          type: intervention.type ?? undefined,
          incentives: intervention.incentives ?? undefined,
          priority: intervention.priority ?? undefined,
          comments: intervention.comments ?? undefined,
          investment: intervention.investment ?? undefined,
          investment_to_evaluated: intervention.investment_to_evaluated ?? false,
          unit_of_measurement: intervention.unit_of_measurement ?? undefined,
          annual_co2_saving: intervention.annual_co2_saving ?? undefined,
          expected_economic_saving: intervention.expected_economic_saving ?? undefined,
          expected_economic_saving_manual:
            (intervention as any).expected_economic_saving_manual ?? false,
          useful_life: intervention.useful_life ?? undefined,
          payback_time: intervention.payback_time ?? undefined,
          payback_time_manual: intervention.payback_time_manual ?? false,
          van_5_10_years: intervention.van_5_10_years ?? undefined,
          van_5_10_years_manual: intervention.van_5_10_years_manual ?? false,
          tir_10_years: intervention.tir_10_years ?? undefined,
          tir_10_years_manual: intervention.tir_10_years_manual ?? false,
          is_to_export: intervention.is_to_export ?? false,
          annual_energy_saving_by_vector: intervention.annual_energy_saving_by_vector ?? {},
          to_evaluated_by_vector: intervention.to_evaluated_by_vector ?? {},
        } as any)
      : undefined,
  });

  const selectedVectorIds = watch('energy_vectors') ?? [];
  const { data: energyVectors = [] } = useDiagnosisEnergyVectorsByDiagnosisId(diagnosisId);
  const { data: vectorTypeOptions = [] } = useVectorTypeOptions();
  const savingByVector = watch('annual_energy_saving_by_vector') ?? {};
  const toEvalByVector = watch('to_evaluated_by_vector') ?? {};
  const investmentToEvaluated = watch('investment_to_evaluated') ?? false;
  const expectedEconomicSavingManual = watch('expected_economic_saving_manual') ?? false;
  const paybackTimeManual = watch('payback_time_manual') ?? false;
  const vanManual = watch('van_5_10_years_manual') ?? false;
  const tirManual = watch('tir_10_years_manual') ?? false;
  const selectedVectors = React.useMemo(
    () => energyVectors.filter(v => selectedVectorIds.includes(v.id!)),
    [energyVectors, selectedVectorIds]
  );

  const { data: energyUsesByVector = {}, isSuccess: energyUsesByVectorReady } = useQuery(
    ['diagnosis-energy-uses-by-vector', diagnosisId],
    () => fetchEnergyUsesByVector(diagnosisId),
    { enabled: open && !!diagnosisId }
  );

  const allowedEnergyUseIds = React.useMemo(() => {
    if (selectedVectorIds.length === 0) return [];
    const set = new Set<string>();
    for (const vectorId of selectedVectorIds) {
      const useIds = energyUsesByVector[vectorId];
      if (useIds) for (const id of useIds) set.add(id);
    }
    return Array.from(set);
  }, [selectedVectorIds, energyUsesByVector]);

  /** Fino a quando la mappa censimenti→usi non è pronta, non filtrare (evita [] che svuota tutto per race). */
  const energyUseFilterOptionIds =
    open && selectedVectorIds.length > 0 && energyUsesByVectorReady
      ? allowedEnergyUseIds
      : undefined;

  React.useEffect(() => {
    if (!open) return;
    if (selectedVectorIds.length === 0) {
      const current = (getValues('energy_uses') ?? []) as string[];
      if (current.length > 0) {
        setValue('energy_uses', [], { shouldDirty: true });
      }
      return;
    }
    if (!energyUsesByVectorReady) return;

    const current = (getValues('energy_uses') ?? []) as string[];
    const allowed = new Set(allowedEnergyUseIds);
    const filtered = current.filter(id => allowed.has(id));
    if (filtered.length !== current.length) {
      setValue('energy_uses', filtered, { shouldDirty: true });
    }
  }, [open, selectedVectorIds, energyUsesByVectorReady, allowedEnergyUseIds, getValues, setValue]);

  React.useEffect(() => {
    if (open) {
      if (intervention) {
        reset({
          diagnosis_id: diagnosisId,
          code: (intervention as any).code ?? undefined,
          energy_vectors: intervention.energy_vectors ?? [],
          title: intervention.title ?? undefined,
          cost_before_ea: intervention.cost_before_ea ?? undefined,
          energy_uses: intervention.energy_uses ?? [],
          all_energy_use: intervention.all_energy_use ?? false,
          description: intervention.description ?? undefined,
          type: intervention.type ?? undefined,
          incentives: intervention.incentives ?? undefined,
          priority: intervention.priority ?? undefined,
          comments: intervention.comments ?? undefined,
          investment: intervention.investment ?? undefined,
          investment_to_evaluated: intervention.investment_to_evaluated ?? false,
          unit_of_measurement: intervention.unit_of_measurement ?? undefined,
          annual_co2_saving: intervention.annual_co2_saving ?? undefined,
          expected_economic_saving: intervention.expected_economic_saving ?? undefined,
          expected_economic_saving_manual:
            (intervention as any).expected_economic_saving_manual ?? false,
          useful_life: intervention.useful_life ?? undefined,
          payback_time: intervention.payback_time ?? undefined,
          payback_time_manual: intervention.payback_time_manual ?? false,
          van_5_10_years: intervention.van_5_10_years ?? undefined,
          van_5_10_years_manual: intervention.van_5_10_years_manual ?? false,
          tir_10_years: intervention.tir_10_years ?? undefined,
          tir_10_years_manual: intervention.tir_10_years_manual ?? false,
          is_to_export: intervention.is_to_export ?? false,
          annual_energy_saving_by_vector: intervention.annual_energy_saving_by_vector ?? {},
          to_evaluated_by_vector: intervention.to_evaluated_by_vector ?? {},
        } as any);
      } else {
        reset({
          diagnosis_id: diagnosisId,
          code: undefined,
          energy_vectors: [],
          title: undefined,
          cost_before_ea: undefined,
          energy_uses: [],
          all_energy_use: false,
          description: undefined,
          type: undefined,
          incentives: undefined,
          priority: undefined,
          comments: undefined,
          investment: undefined,
          investment_to_evaluated: false,
          unit_of_measurement: undefined,
          annual_co2_saving: undefined,
          expected_economic_saving: undefined,
          expected_economic_saving_manual: false,
          useful_life: undefined,
          payback_time: undefined,
          payback_time_manual: false,
          van_5_10_years: undefined,
          van_5_10_years_manual: false,
          tir_10_years: undefined,
          tir_10_years_manual: false,
          is_to_export: false,
          annual_energy_saving_by_vector: {},
          to_evaluated_by_vector: {},
        } as any);
      }
    }
  }, [intervention, diagnosisId, reset, open]);

  const createMutation = useMutation(
    async (data: IDiagnosisInterventionsCreateDTO) => {
      return await apiClient.create_item_diagnosis_interventions__post(data as any);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(`diagnosis-interventions-${diagnosisId}`);
        toast.success('Intervento creato con successo');
        reset();
        onClose();
      },
      onError: (error: any) => {
        toast.error(error?.message || "Errore nella creazione dell'intervento");
      },
    }
  );

  const updateMutation = useMutation(
    async (data: IDiagnosisInterventionsCreateDTO) => {
      if (!intervention?.id) {
        throw new Error('ID intervento mancante');
      }
      return await apiClient.update_item_diagnosis_interventions__item_id__put(
        intervention.id,
        data as any
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(`diagnosis-interventions-${diagnosisId}`);
        toast.success('Intervento aggiornato con successo');
        reset();
        onClose();
      },
      onError: (error: any) => {
        toast.error(error?.message || "Errore nell'aggiornamento dell'intervento");
      },
    }
  );

  const onSubmit = (data: InterventionFormValues) => {
    const submitData: IDiagnosisInterventionsCreateDTO = {
      ...data,
      annual_energy_saving_by_vector: sanitizeAnnualSavingMap(data.annual_energy_saving_by_vector),
    };

    if (intervention) {
      updateMutation.mutate(submitData);
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleClose = () => {
    if (!updateMutation.isLoading && !createMutation.isLoading) {
      reset();
      onClose();
    }
  };

  const isSubmitting = updateMutation.isLoading || createMutation.isLoading;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{intervention ? 'Modifica intervento' : 'Nuovo intervento'}</span>
          <ActionButton
            icon={<CloseIcon />}
            tooltip="Chiudi"
            onClick={handleClose}
            variant="outlined"
          />
        </Box>
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* 1. ID Intervento */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="ID intervento"
                {...register('code')}
                error={!!errors.code}
                helperText={errors.code?.message}
              />
            </Grid>
            {/* 2. Titolo intervento */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Titolo intervento"
                {...register('title')}
                error={!!errors.title}
                helperText={errors.title?.message}
              />
            </Grid>
            {/* 3. Vettori energetici */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="energy_vectors"
                control={control}
                render={({ field, fieldState }) => (
                  <EnergyVectorDropdown
                    diagnosisId={diagnosisId}
                    label="Vettori energetici"
                    value={field.value ?? []}
                    onChange={value => field.onChange(value ?? [])}
                    multiple
                    withUni={false}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    placeholder="Seleziona i vettori energetici"
                  />
                )}
              />
            </Grid>
            {/* 6. Uso energetico (multiplo); "Tutti gli usi energetici" è un'opzione del dropdown → in salvataggio imposta all_energy_use a true */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="energy_uses"
                control={control}
                render={({ field, fieldState }) => {
                  const allEnergyUse = watch('all_energy_use');
                  const dropdownValue = allEnergyUse ? [ALL_ENERGY_USE_VALUE] : (field.value ?? []);
                  return (
                    <EnergyUseDropdown
                      diagnosisId={diagnosisId}
                      label="Usi energetici"
                      value={dropdownValue}
                      onChange={value => {
                        const list = Array.isArray(value) ? value : value != null ? [value] : [];
                        const hasAll = list.includes(ALL_ENERGY_USE_VALUE);
                        setValue('all_energy_use', hasAll, { shouldDirty: true });
                        setValue(
                          'energy_uses',
                          hasAll ? [] : list.filter(id => id !== ALL_ENERGY_USE_VALUE),
                          {
                            shouldDirty: true,
                          }
                        );
                        field.onChange(
                          hasAll ? [] : list.filter(id => id !== ALL_ENERGY_USE_VALUE)
                        );
                      }}
                      multiple
                      isAllEnergyUseVisible
                      filterOptionIds={energyUseFilterOptionIds}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  );
                }}
              />
            </Grid>
            {/* 7. Descrizione */}
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Descrizione"
                {...register('description')}
                error={!!errors.description}
                helperText={errors.description?.message}
                multiline
                rows={6}
              />
            </Grid>
            {/* 8. Tipologia intervento */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="type"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl fullWidth error={!!fieldState.error}>
                    <InputLabel>Tipologia intervento</InputLabel>
                    <Select
                      value={field.value ?? ''}
                      onChange={e => field.onChange(e.target.value)}
                      label="Tipologia intervento"
                    >
                      {TYPE_OPTIONS.map(option => (
                        <MenuItem key={option} value={option}>
                          {option.charAt(0).toUpperCase() + option.slice(1)}
                        </MenuItem>
                      ))}
                    </Select>
                    {fieldState.error && (
                      <FormHelperText>{fieldState.error.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
            {/* 9. Incentivi ottenibili */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Incentivi ottenibili"
                {...register('incentives')}
                error={!!errors.incentives}
                helperText={errors.incentives?.message}
              />
            </Grid>
            {/* 10. Priorità */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="priority"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl fullWidth error={!!fieldState.error}>
                    <InputLabel>Priorità</InputLabel>
                    <Select
                      value={field.value ?? ''}
                      onChange={e => field.onChange(e.target.value)}
                      label="Priorità"
                    >
                      {PRIORITY_OPTIONS.map(option => (
                        <MenuItem key={option} value={option}>
                          {option.charAt(0).toUpperCase() + option.slice(1)}
                        </MenuItem>
                      ))}
                    </Select>
                    {fieldState.error && (
                      <FormHelperText>{fieldState.error.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
            {/* 11. Commenti e valutazioni interne */}
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Commenti e valutazioni interne"
                {...register('comments')}
                error={!!errors.comments}
                helperText={errors.comments?.message}
                multiline
                rows={4}
              />
            </Grid>
            {/* 12. Investimento previsto */}
            <Grid size={{ xs: 12 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <TextField
                  sx={{ flex: 1, maxWidth: 320 }}
                  label="Investimento previsto [€]"
                  {...register('investment', { valueAsNumber: true })}
                  error={!!errors.investment}
                  helperText={errors.investment?.message}
                  type="number"
                  disabled={investmentToEvaluated}
                  inputProps={{ min: 0, step: 1 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={investmentToEvaluated}
                      onChange={e => {
                        const checked = e.target.checked;
                        setValue('investment_to_evaluated', checked, { shouldDirty: true });
                        if (checked) {
                          setValue('investment', undefined, { shouldDirty: true });
                        }
                      }}
                    />
                  }
                  label="Da valutare"
                />
              </Stack>
            </Grid>
            {/* 13. Risparmio energetico atteso: una riga (input + flag) per ogni vettore */}
            {selectedVectors.map(vector => {
              const vectorId = vector.id!;
              const label = getEnergyVectorOptionLabel(vector, vectorTypeOptions);
              const toEval = toEvalByVector[vectorId];
              return (
                <Grid size={{ xs: 12 }} key={vectorId}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <TextField
                      sx={{ flex: 1, maxWidth: 320 }}
                      label={`Risparmio energetico ${label}`}
                      type="number"
                      value={toEval ? '' : (savingByVector[vectorId] ?? '')}
                      onChange={e => {
                        const v = e.target.value === '' ? undefined : Number(e.target.value);
                        setValue(
                          'annual_energy_saving_by_vector',
                          {
                            ...getValues('annual_energy_saving_by_vector'),
                            [vectorId]: v ?? null,
                          },
                          { shouldDirty: true }
                        );
                      }}
                      disabled={!!toEval}
                      inputProps={{ min: 0, step: 1 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!toEval}
                          onChange={e => {
                            const checked = e.target.checked;
                            setValue(
                              'to_evaluated_by_vector',
                              {
                                ...getValues('to_evaluated_by_vector'),
                                [vectorId]: checked,
                              },
                              { shouldDirty: true }
                            );
                            if (checked) {
                              setValue(
                                'annual_energy_saving_by_vector',
                                {
                                  ...getValues('annual_energy_saving_by_vector'),
                                  [vectorId]: null,
                                },
                                { shouldDirty: true }
                              );
                            }
                          }}
                        />
                      }
                      label="Da valutare"
                    />
                  </Stack>
                </Grid>
              );
            })}

            {/* 14. Vita utile intervento */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Vita utile intervento [a]"
                {...register('useful_life', { valueAsNumber: true })}
                error={!!errors.useful_life}
                helperText={errors.useful_life?.message}
                type="number"
              />
            </Grid>
            {/* Risparmio economico annuo atteso [€/a]: calcolato oppure manuale */}
            <Grid size={{ xs: 12 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <TextField
                  sx={{ flex: 1, maxWidth: 320 }}
                  label="Risparmio economico annuo atteso [€/a]"
                  {...register('expected_economic_saving', { valueAsNumber: true })}
                  type="number"
                  disabled={!expectedEconomicSavingManual}
                  inputProps={{ min: 0, step: 1 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={expectedEconomicSavingManual}
                      onChange={e => {
                        const checked = e.target.checked;
                        setValue('expected_economic_saving_manual', checked, { shouldDirty: true });
                        if (!checked) {
                          setValue('expected_economic_saving', undefined, { shouldDirty: true });
                        }
                      }}
                    />
                  }
                  label="Modifica manuale"
                />
              </Stack>
            </Grid>
            {/* Tempo di pay-back [a]: calcolato oppure manuale */}
            <Grid size={{ xs: 12 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <TextField
                  sx={{ flex: 1, maxWidth: 320 }}
                  label="Tempo di pay-back [a]"
                  {...register('payback_time', { valueAsNumber: true })}
                  type="number"
                  disabled={!paybackTimeManual}
                  inputProps={{ min: 0, step: 1 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={paybackTimeManual}
                      onChange={e => {
                        const checked = e.target.checked;
                        setValue('payback_time_manual', checked, { shouldDirty: true });
                        if (!checked) setValue('payback_time', undefined, { shouldDirty: true });
                      }}
                    />
                  }
                  label="Modifica manuale"
                />
              </Stack>
            </Grid>
            {/* VAN 5% 10 anni: calcolato oppure manuale */}
            <Grid size={{ xs: 12 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <TextField
                  sx={{ flex: 1, maxWidth: 320 }}
                  label="VAN 5% 10 anni"
                  {...register('van_5_10_years', { valueAsNumber: true })}
                  type="number"
                  disabled={!vanManual}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={vanManual}
                      onChange={e => {
                        const checked = e.target.checked;
                        setValue('van_5_10_years_manual', checked, { shouldDirty: true });
                        if (!checked) setValue('van_5_10_years', undefined, { shouldDirty: true });
                      }}
                    />
                  }
                  label="Modifica manuale"
                />
              </Stack>
            </Grid>
            {/* TIR 10 anni: calcolato oppure manuale */}
            <Grid size={{ xs: 12 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <TextField
                  sx={{ flex: 1, maxWidth: 320 }}
                  label="TIR 10 anni [%]"
                  {...register('tir_10_years', { valueAsNumber: true })}
                  type="number"
                  disabled={!tirManual}
                  inputProps={{ min: 0, step: 1 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={tirManual}
                      onChange={e => {
                        const checked = e.target.checked;
                        setValue('tir_10_years_manual', checked, { shouldDirty: true });
                        if (!checked) setValue('tir_10_years', undefined, { shouldDirty: true });
                      }}
                    />
                  }
                  label="Modifica manuale"
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Controller
                name="is_to_export"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!field.value}
                        onChange={e => field.onChange(e.target.checked)}
                      />
                    }
                    label="Esporta nel report finale"
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Stack direction="row" spacing={2} justifyContent="center" sx={{ width: '100%', py: 1 }}>
            <ActionButton
              icon={<SaveIcon />}
              tooltip="Salva"
              onClick={handleSubmit(onSubmit)}
              variant="filled"
              disabled={isSubmitting || !isDirty}
              loading={isSubmitting}
            />
          </Stack>
        </DialogActions>
      </form>
    </Dialog>
  );
};
